import { useState } from 'react'
import { Zap, DollarSign, Link2, Wifi, CheckCircle2, ArrowRight } from 'lucide-react'

export default function QuickBooksXeroPage() {
  const [stats] = useState({
    syncStatus: 'Active',
    lastSync: 'Today at 2:30 PM',
    syncedTransactions: 1547,
    integrationUptime: '99.9%',
  })

  const platforms = [
    {
      name: 'QuickBooks Online',
      emoji: '📊',
      status: 'Connected',
      color: 'from-blue-600 to-blue-500',
      features: ['Real-time sync', 'Bank connections', 'Invoice management', 'Expense tracking'],
      lastSync: '2 minutes ago',
      syncedItems: '324 items'
    },
    {
      name: 'QuickBooks Desktop',
      emoji: '💻',
      status: 'Connected',
      color: 'from-cyan-600 to-cyan-500',
      features: ['Local sync', 'File-based sync', 'Multi-user support', 'Full GL access'],
      lastSync: '5 minutes ago',
      syncedItems: '612 items'
    },
    {
      name: 'Xero',
      emoji: '🟢',
      status: 'Connected',
      color: 'from-green-600 to-green-500',
      features: ['Cloud-based sync', 'Multi-currency', 'Real-time reporting', 'Inventory sync'],
      lastSync: '1 minute ago',
      syncedItems: '611 items'
    },
    {
      name: 'Wave Accounting',
      emoji: '🌊',
      status: 'Connected',
      color: 'from-purple-600 to-purple-500',
      features: ['Two-way sync', 'Receipt scanning', 'Invoice creation', 'Report exports'],
      lastSync: '10 minutes ago',
      syncedItems: '289 items'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Zap className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">QuickBooks & Xero Integration</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Seamless Accounting Software Integration</p>
                <p className="text-blue-200 text-sm mt-1">QuickBooks Online • QuickBooks Desktop • Xero • Wave Accounting</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.syncedTransactions}</div>
                <div className="text-sm text-blue-100">Synced Transactions</div>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Sync Status', value: stats.syncStatus, icon: Wifi, color: 'from-green-600 to-green-500' },
            { label: 'Last Sync', value: stats.lastSync, icon: CheckCircle2, color: 'from-blue-600 to-blue-500' },
            { label: 'Synced Today', value: '1,547 items', icon: DollarSign, color: 'from-cyan-600 to-cyan-500' },
            { label: 'Uptime', value: stats.integrationUptime, icon: Zap, color: 'from-purple-600 to-purple-500' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className="text-white text-2xl font-black mt-3">{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Platform Integrations */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Link2 className="w-7 h-7 text-blue-400" />
            <span>Connected Platforms</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {platforms.map((platform, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20"
              >
                <div className={`h-1 w-full bg-gradient-to-r ${platform.color} rounded-full mb-4 shadow-lg`}></div>

                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`bg-gradient-to-br ${platform.color} p-3 rounded-lg shadow-lg text-xl`}>
                      {platform.emoji}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-xl">{platform.name}</h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full inline-block mt-1 bg-green-500/20 text-green-300">
                        {platform.status}
                      </span>
                    </div>
                  </div>
                  <Link2 className="w-5 h-5 text-blue-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                </div>

                <p className="text-cyan-400 text-sm font-semibold mb-2">Last sync: {platform.lastSync}</p>
                <p className="text-blue-300 text-sm font-semibold mb-4">{platform.syncedItems}</p>

                <div className="mb-4">
                  <p className="text-blue-300 text-xs font-semibold mb-2">Features:</p>
                  <div className="flex flex-wrap gap-2">
                    {platform.features.map((feature, fidx) => (
                      <span key={fidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <button className={`w-full py-2.5 px-3 bg-gradient-to-r ${platform.color} hover:shadow-lg rounded-lg text-white text-sm font-bold flex items-center justify-between transition-all group-hover:shadow-lg group-hover:shadow-blue-500/30`}>
                  <span>View Integration</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Sync History & Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sync Schedule */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Zap className="w-5 h-5 text-blue-400" />
              <span>Sync Settings</span>
            </h3>
            <div className="space-y-3">
              {[
                { setting: 'Sync Frequency', value: 'Every 30 minutes' },
                { setting: 'Auto-reconciliation', value: 'Enabled' },
                { setting: 'Data Validation', value: 'Strict' },
                { setting: 'Backup Schedule', value: 'Daily at 2:00 AM' },
                { setting: 'Notification Status', value: 'All Alerts' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <p className="text-white text-sm font-semibold">{item.setting}</p>
                    <span className="text-cyan-400 text-xs font-bold">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Syncs */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Recent Sync Activity</span>
            </h3>
            <div className="space-y-3">
              {[
                { time: 'Today 2:30 PM', platform: 'Xero', items: '89 invoices', status: 'Success' },
                { time: 'Today 2:00 PM', platform: 'QB Online', items: '234 transactions', status: 'Success' },
                { time: 'Today 1:30 PM', platform: 'QB Desktop', items: '156 items', status: 'Success' },
                { time: 'Today 1:00 PM', platform: 'Wave', items: '45 receipts', status: 'Success' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-green-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{item.platform}</p>
                    <span className="bg-green-500/20 text-green-300 text-xs font-bold px-2 py-0.5 rounded">
                      {item.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">{item.time}</span>
                    <span className="text-cyan-400 text-xs font-semibold">{item.items}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
